/* import __COLOCATED_TEMPLATE__ from './scheduled.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type ArticleContent from 'embercom/models/articles/article-content';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import { EntityType } from 'embercom/models/data/entity-types';
import type ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';

interface Signature {
  Args: {
    activeContent: LocalizedKnowledgeContent;
  };
}

export default class Scheduled extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get canUseScheduledPublishing(): boolean {
    return this.appService.app.canUseScheduledPublishing;
  }

  private get articleContent(): ArticleContent | null {
    if (this.args.activeContent.entityType !== EntityType.ArticleContent) {
      return null;
    }
    return this.args.activeContent as unknown as ArticleContent;
  }

  private get pendingUpdates(): ScheduledContentUpdate[] {
    let article = this.articleContent;
    if (!article) {
      return [];
    }
    let updates = article.scheduledContentUpdates;
    let updatesArray: ScheduledContentUpdate[] = Array.isArray(updates)
      ? updates
      : (updates as any)?.toArray() || [];
    return updatesArray.filter((update) => update.status === ScheduledContentUpdateStatus.PENDING);
  }

  private get updateToShow(): ScheduledContentUpdate | undefined {
    let article = this.articleContent;
    if (!article) {
      return undefined;
    }
    let type = article.isPublished ? 'unpublish' : 'publish';
    return this.pendingUpdates.find((update) => update.contentUpdateType === type);
  }

  get label(): string | null {
    let update = this.updateToShow;
    if (!update) {
      return null;
    }

    let key =
      update.contentUpdateType === 'unpublish'
        ? 'knowledge-hub.content-editor.shared.data-section.scheduled-unpublish-label'
        : 'knowledge-hub.content-editor.shared.data-section.scheduled-publish-label';

    return this.intl.t(key);
  }

  get datePrefix(): string | null {
    let update = this.updateToShow;
    if (!update) {
      return null;
    }

    let date = this.intl.formatDate(update.changeAt, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: this.appService.app.timezone,
    });

    return this.intl.t('knowledge-hub.content-editor.shared.data-section.scheduled-date-prefix', {
      date,
    });
  }

  get time(): string | null {
    let update = this.updateToShow;
    if (!update) {
      return null;
    }

    return this.intl.formatTime(update.changeAt, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: this.appService.app.timezone,
      timeZoneName: 'short',
    });
  }

  get shouldRender(): boolean {
    return this.canUseScheduledPublishing && this.label !== null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::DataSection::Scheduled': typeof Scheduled;
    'knowledge-hub/content-editor/shared/data-section/scheduled': typeof Scheduled;
  }
}
